import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';
import { Link } from 'react-router-dom';

interface Speaker {
  id: string;
  name: string;
  email: string;
  description: string;
  profileImageUrl?: string;
}

interface SpeakerSearchResponse {
  speakers: Speaker[];
  total_results: number;
  search_query?: string;
  message?: string;
}

const SpeakerSearchPage: React.FC = () => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    loadSpeakers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadSpeakers = async (query?: string) => {
    try {
      setIsLoading(true);
      setError(null); // Clear error state before making new request

      const params: any = {
        page: currentPage,
        limit: 20
      };

      if (query) {
        params.search = query;
      }

      const response = await apiClient.get('/v1/users/speakers', { params });
      const data: SpeakerSearchResponse = response.data.data;

      setSpeakers(data.speakers || []);
      setTotalResults(data.total_results || 0);

      // Check if there are more results
      const pagination = response.data.metadata?.pagination;
      setHasMore(pagination?.has_next || false);

    } catch (err: any) {
      console.error('Failed to load speakers:', err);

      // Only set error for actual failures, not empty results
      if (err.response) {
        // Server responded with error status (4xx, 5xx)
        setError(err.response.data?.error?.message || 'Failed to load speakers');
      } else if (err.request) {
        // Network error - no response received
        setError('Network error. Please check your connection.');
      } else {
        // Other errors
        setError('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadSpeakers(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    loadSpeakers();
  };

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  if (isLoading && speakers.length === 0) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5>Loading Speakers...</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-10">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">
                <i className="fas fa-users me-2"></i>
                Find Speakers
              </h4>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Search Form */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search speakers by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button className="btn btn-primary" type="submit">
                    <i className="fas fa-search me-1"></i>
                    Search
                  </button>
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={handleClearSearch}
                    >
                      <i className="fas fa-times me-1"></i>
                      Clear
                    </button>
                  )}
                </div>
              </form>

              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                Only users who have enabled their speaker toggle appear in these results.
              </div>

              {/* Results Count */}
              <div className="mb-3">
                <strong>{totalResults}</strong> speaker{totalResults !== 1 ? 's' : ''} found
              </div>

              {/* Speaker Results */}
              {speakers.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-user-slash fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No speakers found</h5>
                  {searchQuery && (
                    <p className="text-muted">
                      Try adjusting your search criteria
                    </p>
                  )}
                </div>
              ) : (
                <div className="row">
                  {speakers.map((speaker) => (
                    <div key={speaker.id} className="col-md-6 mb-3">
                      <div className="card h-100">
                        <div className="card-body">
                          <div className="d-flex align-items-start">
                            <div className="me-3">
                              {speaker.profileImageUrl ? (
                                <img
                                  src={speaker.profileImageUrl}
                                  alt={speaker.name}
                                  className="rounded-circle"
                                  style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                                  style={{ width: '60px', height: '60px' }}
                                >
                                  <i className="fas fa-user fa-2x"></i>
                                </div>
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="card-title mb-1">{speaker.name}</h5>
                              <p className="text-muted small mb-2">
                                <i className="fas fa-envelope me-1"></i>
                                {speaker.email}
                              </p>
                              {speaker.description && (
                                <p className="card-text small">
                                  {speaker.description.length > 150
                                    ? `${speaker.description.substring(0, 150)}...`
                                    : speaker.description}
                                </p>
                              )}
                              <Link
                                to={`/users/${speaker.id}/profile`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="fas fa-user me-1"></i>
                                View Profile
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {speakers.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <button
                    className="btn btn-outline-primary"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1 || isLoading}
                  >
                    <i className="fas fa-chevron-left me-1"></i>
                    Previous
                  </button>
                  <span>Page {currentPage}</span>
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleNextPage}
                    disabled={!hasMore || isLoading}
                  >
                    Next
                    <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeakerSearchPage;
